import { openDB, deleteDB } from 'idb'

export default {
    initDB(dbName) {
        let dbInstance = null
        let currentVersion = null

        // Get current DB version
        async function getCurrentVersion() {
            try {
                const db = await openDB(dbName)
                const version = db.version
                db.close()
                return version
            } catch (e) {
                return 1
            }
        }

        // Open DB (initial or upgraded)
        async function openOrUpgrade(storeName) {
            if (!dbInstance) {
                currentVersion = await getCurrentVersion()
                dbInstance = await openDB(dbName, currentVersion)
            }

            // Store already exists
            if (dbInstance.objectStoreNames.contains(storeName)) {
                return dbInstance
            }

            // Need upgrade to add new store
            dbInstance.close()
            currentVersion += 1

            dbInstance = await openDB(dbName, currentVersion, {
                upgrade(upgradedDb) {
                    if (!upgradedDb.objectStoreNames.contains(storeName)) {
                        upgradedDb.createObjectStore(storeName, {
                            keyPath: 'id',
                            autoIncrement: true,
                        })
                    }
                },
            })

            return dbInstance
        }

        const withDB = async (storeName, action) => {
            const db = await openOrUpgrade(storeName)
            return action(db, storeName)
        }

        return {
            store(storeName) {
                return {
                    async create(item) {
                        return withDB(storeName, (db, store) => db.add(store, item))
                    },
                    async read(id) {
                        return withDB(storeName, (db, store) => db.get(store, id))
                    },
                    async readAll() {
                        return withDB(storeName, (db, store) => db.getAll(store))
                    },
                    async update(item) {
                        return withDB(storeName, (db, store) => db.put(store, item))
                    },
                    async delete(id) {
                        return withDB(storeName, (db, store) => db.delete(store, id))
                    },
                    async clear() {
                        return withDB(storeName, (db, store) => db.clear(store))
                    },
                    async where(predicate) {
                        return withDB(storeName, async (db, store) => {
                            const all = await db.getAll(store)
                            return all.filter(predicate)
                        })
                    },
                    async find(predicate) {
                        return withDB(storeName, async (db, store) => {
                            const all = await db.getAll(store)
                            return all.find(predicate)
                        })
                    },
                }
            },
            async deleteDB() {
                if (dbInstance) {
                    dbInstance.close()
                }
                await deleteDB(dbName)
                dbInstance = null
                currentVersion = null
            },
        }
    },
}
